import { useEffect } from 'react';

interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  structuredData?: object;
}

export const useSEO = (seoData: SEOData) => {
  useEffect(() => {
    // Title
    document.title = seoData.title;

    // Meta tags
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Basic meta tags
    updateMetaTag('description', seoData.description);
    if (seoData.keywords) {
      updateMetaTag('keywords', seoData.keywords);
    }

    // Canonical URL
    if (seoData.canonical) {
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = seoData.canonical;
    }

    // Open Graph tags
    updateMetaTag('og:title', seoData.ogTitle || seoData.title, true);
    updateMetaTag('og:description', seoData.ogDescription || seoData.description, true);
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:url', window.location.href, true);
    if (seoData.ogImage) {
      updateMetaTag('og:image', seoData.ogImage, true);
    }

    // Twitter Card tags
    updateMetaTag('twitter:card', seoData.twitterCard || 'summary_large_image');
    updateMetaTag('twitter:title', seoData.twitterTitle || seoData.title);
    updateMetaTag('twitter:description', seoData.twitterDescription || seoData.description);
    if (seoData.twitterImage) {
      updateMetaTag('twitter:image', seoData.twitterImage);
    }

    // Last modified
    updateMetaTag('last-modified', new Date().toISOString());

    // Structured Data
    if (seoData.structuredData) {
      let script = document.querySelector('script[type="application/ld+json"]');
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(seoData.structuredData);
    }

    // Cleanup function
    return () => {
      // Remove structured data script when component unmounts
      const structuredDataScript = document.querySelector('script[type="application/ld+json"]');
      if (structuredDataScript) {
        structuredDataScript.remove();
      }
    };
  }, [seoData]);
};

export const generateOrganizationSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "IDS教習所グループ",
    "url": "https://ids-group.jp/",
    "logo": "https://static.readdy.ai/image/b1b0be4a533f8dd52f529875d332f87d/0fa5f3ef0554dd0c026b275b14f8a7ae.png",
    "description": "佐賀・福岡筑後・鹿児島でDJI農業ドローン、FJD農機自動操舵システム、Farmo水管理システムを販売するスマート農業機器専門企業",
    "telephone": "+81-952-71-9855",
    "email": "info@ids-group.jp",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "多久市",
      "addressRegion": "佐賀県",
      "addressCountry": "JP"
    },
    "areaServed": [
      {
        "@type": "State",
        "name": "佐賀県"
      },
      {
        "@type": "Place",
        "name": "福岡県筑後地域"
      },
      {
        "@type": "State",
        "name": "鹿児島県"
      }
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+81-952-71-9855",
      "email": "info@ids-group.jp",
      "contactType": "customer service",
      "availableLanguage": "Japanese",
      "areaServed": "JP"
    },
    "subOrganization": [
      {
        "@type": "Organization",
        "name": "IDS佐賀教習所",
        "telephone": "+81-952-71-9855",
        "parentOrganization": {
          "@type": "Organization",
          "name": "IDS教習所グループ"
        }
      },
      {
        "@type": "Organization",
        "name": "IDS川副教習所",
        "telephone": "+81-952-97-5291",
        "parentOrganization": {
          "@type": "Organization",
          "name": "IDS教習所グループ"
        }
      },
      {
        "@type": "Organization",
        "name": "IDS鹿児島教習所",
        "telephone": "+81-99-213-9466",
        "parentOrganization": {
          "@type": "Organization",
          "name": "IDS教習所グループ"
        }
      }
    ],
    "sameAs": []
  };
};

export const generateWebPageSchema = (title: string, description: string, url: string) => {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": description,
    "url": url,
    "isPartOf": {
      "@type": "WebSite",
      "name": "IDS教習所グループ",
      "url": "https://ids-group.jp"
    },
    "about": {
      "@type": "Organization",
      "name": "IDS教習所グループ"
    }
  };
};

export const generateProductSchema = (name: string, description: string, image?: string) => {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": name,
    "description": description,
    "image": image || "https://ids-group.jp/images/default-product.jpg",
    "brand": {
      "@type": "Brand",
      "name": "IDS教習所グループ"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "IDS教習所グループ"
    }
  };
};

export const generateFAQSchema = (faqs: Array<{question: string, answer: string}>) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

export const generateArticleSchema = (title: string, description: string, datePublished: string, dateModified?: string) => {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    "author": {
      "@type": "Organization",
      "name": "IDS教習所グループ"
    },
    "publisher": {
      "@type": "Organization",
      "name": "IDS教習所グループ",
      "logo": {
        "@type": "ImageObject",
        "url": "https://ids-group.jp/images/logo.png"
      }
    }
  };
};
